import { useState, useRef, useCallback } from 'react';

import { useBroadcastChannel } from './use-broadcast-channel';

export interface AuthBroadcastMessage {
  type:
    | 'TOKEN_EXPIRED'
    | 'TOKEN_REFRESHED'
    | 'USER_LOGGED_OUT'
    | 'USER_LOGGED_IN'
    | 'REFRESH_TOKEN_EXPIRED'
    | 'HIDE_TOKEN_POPUP';
  payload?: {
    token?: string;
    tokenExpiration?: number;
    userEmail?: string;
    tabId?: string;
  };
}

export const useAuthBroadcast = () => {
  const tabIdRef = useRef<string>(Math.random().toString(36).substr(2, 9));
  const [authMessages, setAuthMessages] = useState<AuthBroadcastMessage[]>([]);

  const handleAuthMessage = useCallback((message: AuthBroadcastMessage) => {
    setAuthMessages((prev) => [...prev, message]);
  }, []);

  const { isLeader, sendMessage } = useBroadcastChannel<AuthBroadcastMessage>('auth-sync', handleAuthMessage);

  const broadcastTokenExpired = useCallback(() => {
    sendMessage({
      type: 'TOKEN_EXPIRED',
      payload: { tabId: tabIdRef.current },
    });
  }, [sendMessage]);

  const broadcastTokenRefreshed = useCallback(
    (token: string, tokenExpiration: number, userEmail: string) => {
      sendMessage({
        type: 'TOKEN_REFRESHED',
        payload: {
          token,
          tokenExpiration,
          userEmail,
          tabId: tabIdRef.current,
        },
      });
    },
    [sendMessage]
  );

  const broadcastUserLoggedOut = useCallback(() => {
    sendMessage({
      type: 'USER_LOGGED_OUT',
      payload: { tabId: tabIdRef.current },
    });
  }, [sendMessage]);

  const broadcastUserLoggedIn = useCallback(
    (token: string, tokenExpiration: number, userEmail: string) => {
      sendMessage({
        type: 'USER_LOGGED_IN',
        payload: {
          token,
          tokenExpiration,
          userEmail,
          tabId: tabIdRef.current,
        },
      });
    },
    [sendMessage]
  );

  const broadcastRefreshTokenExpired = useCallback(() => {
    sendMessage({
      type: 'REFRESH_TOKEN_EXPIRED',
      payload: { tabId: tabIdRef.current },
    });
  }, [sendMessage]);

  const broadcastHideTokenPopup = useCallback(() => {
    sendMessage({
      type: 'HIDE_TOKEN_POPUP',
      payload: { tabId: tabIdRef.current },
    });
  }, [sendMessage]);

  return {
    tabId: tabIdRef.current,
    isLeader,
    authMessages,
    broadcastTokenExpired,
    broadcastTokenRefreshed,
    broadcastUserLoggedOut,
    broadcastUserLoggedIn,
    broadcastRefreshTokenExpired,
    broadcastHideTokenPopup,
  };
};
